import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { api, API_BASE } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { useAuth } from '../lib/auth-context';
import { ROLE_LABELS } from '../lib/constants';
import { Copy, Check, Globe2, Webhook, Users as UsersIcon, Mail, Eye, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';

// ★ 2026-08-04 (Fase 33.3) — Master Email Template editor (Richard/Eufemia only)
const MASTER_TEMPLATE_EDITORS = new Set([
  'legal@housecashflows.com',
  'eufemia@housecashflows.com',
]);

function MasterEmailTemplateSection() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [headerHtml, setHeaderHtml] = useState('');
  const [footerHtml, setFooterHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/email-template/master');
      setData(r.data);
      setHeaderHtml(r.data.header_html || '');
      setFooterHtml(r.data.footer_html || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('settings.tplLoadError', 'Errore caricamento template'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch('/email-template/master', {
        header_html: headerHtml,
        footer_html: footerHtml,
      });
      setData(r.data);
      toast.success(t('settings.tplSaved', 'Template email master aggiornato'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('settings.tplSaveError', 'Errore salvataggio'));
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm(t('settings.tplResetConfirm', 'Vuoi davvero riportare il template ai valori di default?'))) return;
    setSaving(true);
    try {
      const r = await api.post('/email-template/master/reset');
      setData(r.data);
      setHeaderHtml(r.data.header_html || '');
      setFooterHtml(r.data.footer_html || '');
      toast.success(t('settings.tplResetDone', 'Template ripristinato ai valori di default'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('settings.tplResetError', 'Errore reset'));
    } finally { setSaving(false); }
  };

  const sampleBody = `<p>Ciao <b>Mario Rossi</b>,</p>
<p>Grazie per il tempo dedicato durante la nostra call. Come promesso, ti alleghiamo la proposta dettagliata per il progetto <b>Green Paradise Albania — Equity 50k</b>.</p>
<ul>
  <li><b>Country:</b> Albania</li>
  <li><b>Path:</b> Equity</li>
  <li><b>Prezzo:</b> € 50.000</li>
  <li><b>Metodo di pagamento:</b> Bonifico 50% alla firma + 3 rate a 30/60/90 gg</li>
</ul>
<p>Restiamo a disposizione per qualsiasi chiarimento.</p>`;

  const previewHtml = `${headerHtml || ''}\n${sampleBody}\n${footerHtml || ''}`;

  if (loading) return <Card><CardContent className="p-4 text-sm text-muted-foreground">{t('settings.tplLoading', 'Caricamento template…')}</CardContent></Card>;

  return (
    <Card className="border-border" data-testid="settings-master-template">
      <CardContent className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            <div className="font-medium">{t('settings.tplTitle', 'Template Email Master')}</div>
            {data?.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">Default</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={reset} disabled={saving} data-testid="master-template-reset">
              <RotateCcw className="w-4 h-4 mr-1" />{t('settings.tplResetBtn', 'Ripristina default')}
            </Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="master-template-save">
              <Save className="w-4 h-4 mr-1" />
              {saving ? t('common.saving', 'Salvataggio…') : t('common.save', 'Salva')}
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-3">{t('settings.tplHint', 'Header e footer HTML applicati automaticamente a tutte le email proposte inviate dal CRM. Il corpo di ogni proposta viene incapsulato tra header e footer.')}</p>
        {data?.updated_by && (
          <div className="text-[10px] text-muted-foreground mb-3">
            {t('settings.tplLastUpdate', 'Ultimo aggiornamento:')} {new Date(data.updated_at).toLocaleString(i18n.language === 'es' ? 'es-ES' : 'it-IT')} · {data.updated_by}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Editors */}
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase">Header HTML *</Label>
              <Textarea
                rows={10}
                value={headerHtml}
                onChange={(e) => setHeaderHtml(e.target.value)}
                className="font-mono text-[11px] mt-1"
                placeholder="&lt;!DOCTYPE html&gt;..."
                data-testid="master-template-header"
              />
            </div>
            <div>
              <Label className="text-xs uppercase">Footer HTML *</Label>
              <Textarea
                rows={8}
                value={footerHtml}
                onChange={(e) => setFooterHtml(e.target.value)}
                className="font-mono text-[11px] mt-1"
                placeholder="&lt;/table&gt;&lt;/body&gt;..."
                data-testid="master-template-footer"
              />
            </div>
          </div>

          {/* Live preview */}
          <div>
            <Label className="text-xs uppercase flex items-center gap-1 mb-1">
              <Eye className="w-3 h-3" /> {t('settings.tplPreviewLive', 'Anteprima live')}
            </Label>
            <div className="rounded-md border border-border overflow-hidden bg-white">
              <div className="bg-slate-100 text-[10px] px-2 py-1 border-b border-border text-muted-foreground">
                {t('settings.tplPreviewCaption', 'Preview: header + esempio proposta + footer')}
              </div>
              <iframe
                title="Master Email Template Preview"
                srcDoc={previewHtml}
                className="w-full h-[520px] bg-white"
                sandbox=""
                data-testid="master-template-preview"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'commerciale' });
  const [copied, setCopied] = useState('');

  useEffect(() => { api.get('/users').then((r) => setUsers(r.data)); }, []);

  const create = async () => {
    if (!form.email || !form.password || !form.name) { toast.error(t('settings.fillAll', 'Compila tutti i campi')); return; }
    try {
      await api.post('/auth/register', form);
      toast.success(t('settings.userCreated', 'Utente creato'));
      setForm({ email: '', password: '', name: '', role: 'commerciale' });
      const r = await api.get('/users'); setUsers(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || t('common.error', 'Errore')); }
  };

  const copy = (t, key) => { navigator.clipboard?.writeText(t); setCopied(key); setTimeout(() => setCopied(''), 1500); };

  const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const webhooks = [
    { label: 'Meta Lead Ads', url: `${baseUrl}/api/webhooks/meta` },
    { label: 'TikTok Lead Gen', url: `${baseUrl}/api/webhooks/tiktok` },
    { label: 'Google Lead Forms', url: `${baseUrl}/api/webhooks/google` },
    { label: t('settings.whGenericApi', 'API generica (Zapier/Make)'), url: `${baseUrl}/api/webhooks/lead` },
    { label: 'Resend events', url: `${baseUrl}/api/webhooks/resend` },
    { label: t('settings.whWhatsapp', 'WhatsApp inbound (dai vostri AI agent)'), url: `${baseUrl}/api/webhooks/whatsapp/inbound` },
  ];

  const isAdmin = user?.role === 'admin';
  const isMasterEditor = MASTER_TEMPLATE_EDITORS.has((user?.email || '').toLowerCase());

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div>
        <h1 className="font-serif text-2xl text-[hsl(var(--primary))]" data-testid="settings-title">{t('settings.title', 'Impostazioni')}</h1>
        <p className="text-xs text-muted-foreground">{t('settings.subtitle', 'Utenti, integrazioni e webhook')}</p>
      </div>

      {isMasterEditor && (
        <Card className="border-border" data-testid="settings-email-automations-link">
          <CardContent className="p-4 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[hsl(var(--primary))]" />
              <div>
                <div className="font-medium">{t('settings.autoEmails', 'Email Automatiche')}</div>
                <div className="text-xs text-muted-foreground">{t('settings.autoEmailsHint', 'Crea e gestisci le email che partono al cambio stage, dopo N giorni di inattività, o al cambio cluster HCF.')}</div>
              </div>
            </div>
            <div className="ml-auto">
              <Button size="sm" onClick={() => window.location.href = '/app/email-automations'} data-testid="open-email-automations">
                {t('settings.openManage', 'Apri gestione')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isMasterEditor && <MasterEmailTemplateSection />}

      <Card className="border-border" data-testid="settings-webhooks">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3"><Webhook className="w-4 h-4" /><div className="font-medium">{t('settings.webhooksTitle', 'Webhooks d’ingresso')}</div></div>
          <p className="text-xs text-muted-foreground mb-3">{t('settings.webhooksHint', 'Inserisci questi URL nelle tue integrazioni per ricevere lead/eventi automaticamente.')}</p>
          <div className="space-y-2">
            {webhooks.map((w) => (
              <div key={w.url} className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="text-xs text-muted-foreground">{w.label}</div>
                  <div className="font-mono text-[11px] truncate bg-[var(--surface-2)] border border-border rounded px-2 py-1">{w.url}</div>
                </div>
                <Button size="sm" variant="secondary" onClick={() => copy(w.url, w.url)} data-testid="copy-webhook">
                  {copied === w.url ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-border" data-testid="settings-public-links">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3"><Globe2 className="w-4 h-4" /><div className="font-medium">{t('settings.publicLinks', 'Link pubblici da condividere ai lead')}</div></div>
          <ul className="text-sm space-y-1">
            <li>{t('settings.linkModulo2', 'Modulo 2 qualificazione:')} <a className="font-mono text-xs text-[hsl(var(--primary))] underline" href="/modulo2" target="_blank" rel="noreferrer">/modulo2</a></li>
            <li>{t('settings.linkVslInitial', 'VSL Iniziale:')} <a className="font-mono text-xs text-[hsl(var(--primary))] underline" href="/vsl/initial" target="_blank" rel="noreferrer">/vsl/initial</a></li>
            <li>VSL Equity Estate: <a className="font-mono text-xs text-[hsl(var(--primary))] underline" href="/vsl/equity" target="_blank" rel="noreferrer">/vsl/equity</a></li>
            <li>VSL Real Estate (Verification Pass): <a className="font-mono text-xs text-[hsl(var(--primary))] underline" href="/vsl/real-estate" target="_blank" rel="noreferrer">/vsl/real-estate</a></li>
            <li>{t('settings.linkBooking', 'Booking call:')} <a className="font-mono text-xs text-[hsl(var(--primary))] underline" href="/booking" target="_blank" rel="noreferrer">/booking</a></li>
            <li>{t('settings.linkLanding', 'Landing pubblica:')} <a className="font-mono text-xs text-[hsl(var(--primary))] underline" href="/" target="_blank" rel="noreferrer">/</a></li>
          </ul>
        </CardContent>
      </Card>

      <Card className="border-border" data-testid="settings-users">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3"><UsersIcon className="w-4 h-4" /><div className="font-medium">{t('settings.teamUsers', 'Utenti del team')}</div></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--surface-2)] border-b border-border text-left">
                <tr><th className="px-3 py-2 text-xs text-muted-foreground">{t('settings.colName', 'Nome')}</th><th className="px-3 py-2 text-xs text-muted-foreground">Email</th><th className="px-3 py-2 text-xs text-muted-foreground">{t('settings.colRole', 'Ruolo')}</th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-border" data-testid="user-row">
                    <td className="px-3 py-2">{u.name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{u.email}</td>
                    <td className="px-3 py-2">{ROLE_LABELS[u.role] || u.role}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isAdmin && (
            <div className="mt-4 p-4 rounded-md border border-dashed border-border bg-[var(--surface-2)]">
              <div className="text-sm font-medium mb-2">{t('settings.createUser', 'Crea nuovo utente')}</div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <Input placeholder={t('settings.colName', 'Nome')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" />
                <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" />
                <Input placeholder="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" />
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="commerciale">Commerciale</SelectItem>
                    <SelectItem value="legale">Legale</SelectItem>
                    <SelectItem value="amministrativo">Amministrativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-2 flex justify-end">
                <Button onClick={create} data-testid="create-user-button">{t('settings.createUserBtn', 'Crea utente')}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
